import { useFocusReturn } from '@mantine/hooks';
import type { Editor } from '@tiptap/react';
import { motion } from 'motion/react';
import { useActionState, useRef } from 'react';
import { useImmer } from 'use-immer';

import classes from './ChatInputForm.module.css';
import { ChatInputTextEditor } from './ChatInputTextEditor';
import type { ChatFormState } from './types';

export interface ChatInputFormProps {
  onSubmit: (message: string) => Promise<boolean>;
}

export function ChatInputForm({ onSubmit }: ChatInputFormProps) {
  const [editorContents, setEditorContents] = useImmer('');
  const formRef = useRef<HTMLFormElement | null>(null);
  const [currentInput, setCurrentInput] = useImmer('');

  const [, formAction, isPending] = useActionState<ChatFormState, FormData>(
    async (_prevState: ChatFormState, _payload: FormData) => {
      const message = editorContents;
      if (!message) {
        return {};
      }
      const result = await onSubmit(message.toString());
      if (result) {
        setCurrentInput('');
        setEditorContents('');
      }
      return result;
    },
    {},
  );

  useFocusReturn({ opened: isPending });

  return (
    <form action={formAction} className={classes.form} ref={formRef}>
      <motion.div
        animate={{ y: 0, opacity: 1, scale: 1 }}
        initial={{ y: 100, opacity: 0, scale: 0.8 }}
        transition={{ duration: 1 }}
      >
        <ChatInputTextEditor
          value={currentInput}
          onChange={(value: Editor) => {
            setCurrentInput(value.getHTML());
            setEditorContents(value.getText());
          }}
          onSubmit={() => {
            formRef.current?.requestSubmit();
          }}
        />
      </motion.div>
    </form>
  );
}
